import React, { useCallback, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import CopyToClipboard from 'react-copy-to-clipboard';
import type { AppState } from '~/reducers';
import URLS from '~/constants/urls';
import GoogleAnalyticsConstants from '~/constants/google-analytics-constants';
import AnimatedCheckmark from '~/components/views/AnimatedCheckmark/AnimatedCheckmark';
import PrimaryButton from '~/components/views/common/buttons/PrimaryButton/PrimaryButton';
import SecondaryButton from '~/components/views/common/buttons/SecondaryButton/SecondaryButton';
import ModalContent from '~/components/views/common/Modal/components/ModalContent';
import { PortalModal } from '~/components/views/common/Modal/Modal';
import FlashMessage from '~/components/views/FlashMessage/FlashMessage';
import FormattedMessageWrapper from '~/components/views/FormattedMessageWrapper/FormattedMessageWrapper';
import LinkBlankTarget from '~/components/views/LinkBlankTarget/LinkBlankTarget';
import { toggleModalOpen as toggleModalOpenAction } from './reducer';

import style from './BuySensorModalContainer.module.pcss';

export type Props = ReduxProps;
export const BuySensorModalContainer = ({
  promoCode,
  toggleModalOpen,
}: Props) => {
  const [isCopied, setIsCopied] = useState(false);

  const onPromoCodeClick = useCallback(() => {
    setIsCopied(true);
  }, []);

  const resetIsCopied = useCallback(() => {
    setIsCopied(false);
  }, []);

  const closeModal = useCallback(() => {
    toggleModalOpen();
  }, [toggleModalOpen]);

  return (
    <PortalModal
      isOpen={true}
      className={style.BuySensorModalContainer}
      trackingLabel="BuySensorModal"
      closeOnEscapeKey={true}
      closeOnOverlayClick={true}
      close={closeModal}
    >
      <ModalContent>
        <FormattedMessageWrapper langKey="web.buy_sensor.modal.title" />

        <CopyToClipboard text={promoCode} onCopy={onPromoCodeClick}>
          <div
            className={style.promoCodeWrapper}
            data-trackingaction={GoogleAnalyticsConstants.actions.CLICK}
            data-trackinglabel="CopyPromoCode"
          >
            <FlashMessage
              isVisible={isCopied}
              onHide={resetIsCopied}
              timeoutTimer={1500}
            >
              <div className={style.copiedFlashMessage}>
                <AnimatedCheckmark size={32} />
                <FormattedMessageWrapper langKey="web.buy_sensor.modal.promo_code.text_copied" />
              </div>
            </FlashMessage>
            <FormattedMessageWrapper langKey="web.buy_sensor.modal.promo_code.click_to_copy" />
            <h2>{promoCode}</h2>
          </div>
        </CopyToClipboard>

        <div className={style.buttonWrapper}>
          <LinkBlankTarget
            href={URLS.HUSQVARNA_US_SHOP_SENSOR}
            trackingLabel="GoToShop"
            onClick={closeModal}
          >
            <PrimaryButton
              className="bold uppercase"
              langKey="web.buy_sensor.modal.button.go_to_shop"
            />
          </LinkBlankTarget>

          <SecondaryButton
            className="bold uppercase"
            langKey="web.buy_sensor.modal.button.skip"
            trackingLabel="Cancel"
            onClick={closeModal}
          />
        </div>
      </ModalContent>
    </PortalModal>
  );
};

const mapDispatchToProps = {
  toggleModalOpen: toggleModalOpenAction,
};

const mapStateToProps = ({ buySensor }: AppState) => ({
  promoCode: buySensor.promoCode,
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;
export default connector(BuySensorModalContainer);
